import { t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { useState } from "react";

import type { DiagnosticLu } from "../session/drapeaux.js";

/**
 * `P0-PLT-038`'s console half: **the four columns that make one refusal
 * legible.**
 *
 * **Why it exists.** On 28 August 2026 a founder on a hard-refreshed staging
 * console saw no demonstration card and had no way to find out why. Three
 * different causes , the flag off, the environment excluded, the marker missing
 * , all look identical from every screen this product had. The incident of 24
 * August had even recorded the flag's raw value as `true`, which was correct
 * and which settled nothing, because the derived expression also joins a table
 * that was empty.
 *
 * So this card shows, per flag, the same four parts the server evaluates: the
 * raw value, the environments the flag may be true in, the marker this database
 * carries, and the derived answer. **A figure displayed but not derived is a
 * blind witness with a title**, and the fix for a blind witness is to show it
 * the whole expression.
 *
 * **The marker line is above the table on purpose.** When it reads "absent",
 * every derived answer below is false for one reason and not for eleven, and a
 * reader should be told that before they read eleven rows.
 *
 * **It renders on the RIGHT, not on a flag**, which is the one honest choice
 * here: a diagnostic gated behind the state it diagnoses would vanish exactly
 * when it is needed. The route's own `platform.flags.admin` check is the
 * enforcement; this is the courtesy of not showing a control to somebody the
 * server would refuse.
 */

export function DiagnosticDrapeaux({
  lireLesDrapeaux,
}: {
  /** Reads the four columns. `null` when the API refuses the read. */
  readonly lireLesDrapeaux: () => Promise<DiagnosticLu | null>;
}): React.JSX.Element {
  const [lu, setLu] = useState<DiagnosticLu | null>(null);
  const [refuse, setRefuse] = useState(false);
  const [enCours, setEnCours] = useState(false);

  return (
    <Card shape="console" className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-gray-900">{t("console.drapeaux.titre")}</h2>
      <p className="text-sm text-gray-500">{t("console.drapeaux.explication")}</p>
      <Button
        variant="secondary"
        onClick={() => {
          setEnCours(true);
          void lireLesDrapeaux()
            .then((reponse) => {
              setLu(reponse);
              setRefuse(reponse === null);
            })
            .finally(() => {
              setEnCours(false);
            });
        }}
      >
        {enCours ? t("console.drapeaux.lecture") : t("console.drapeaux.lire")}
      </Button>

      {lu === null ? null : (
        <>
          <p role="status" className="text-sm font-semibold text-gray-900">
            {lu.marker === null ? t("console.drapeaux.marqueurAbsent") : t("console.drapeaux.marqueur")}
            {lu.marker === null ? "" : ` ${lu.marker}`}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-1 pr-3 font-normal">{t("console.drapeaux.colonneCle")}</th>
                  <th className="py-1 pr-3 font-normal">{t("console.drapeaux.colonneValeur")}</th>
                  <th className="py-1 pr-3 font-normal">{t("console.drapeaux.colonneEnvironnements")}</th>
                  <th className="py-1 pr-3 font-normal">{t("console.drapeaux.colonneMarqueur")}</th>
                  <th className="py-1 font-normal">{t("console.drapeaux.colonneDerive")}</th>
                </tr>
              </thead>
              <tbody>
                {lu.data.map((drapeau) => (
                  <tr key={drapeau.key} className="border-t border-gray-100 text-gray-900">
                    <td className="py-1 pr-3 font-mono text-xs">{drapeau.key}</td>
                    <td className="py-1 pr-3">
                      {drapeau.valueBoolean === true
                        ? t("console.drapeaux.vrai")
                        : t("console.drapeaux.faux")}
                    </td>
                    <td className="py-1 pr-3 text-xs">
                      {drapeau.allowedEnvironments.length === 0
                        ? t("console.drapeaux.aucunEnvironnement")
                        : drapeau.allowedEnvironments.join(", ")}
                    </td>
                    <td className="py-1 pr-3 text-xs">{lu.marker ?? t("console.drapeaux.absent")}</td>
                    <td className="py-1 font-semibold">
                      {drapeau.enabled ? t("console.drapeaux.actif") : t("console.drapeaux.inactif")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {refuse ? (
        <p role="alert" className="text-sm text-gray-500">
          {t("console.drapeaux.refuse")}
        </p>
      ) : null}
    </Card>
  );
}
